import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Link from './RefreshLink';
import { ManagerModeBar, useManagerSession } from './ManagerMode';
import { IcHome, IcShoppingCart, IcShoppingBag, IcPackageOpen, IcUsers, IcUserCog, IcFileText, IcTruck, IcLogOut, IcSettings, IcLineChart, IcBarChart, IcCpu } from '../theme/icons';
import authService from '../services/authService';
import { useEngine } from '../desktop/engine';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navRef = useRef(null);
  
  // State to track active dropdown menu (only one can be open at a time)
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Get user permissions and info
  useManagerSession(); // la caisse en mode manager voit tout : re-rendu au début / à la fin
  const isAdmin = authService.hasRole(['admin']);
  const hasFacturationAccess = authService.hasFacturationAccess();
  // Desktop app (local engine present): the « Ce poste » page is reachable.
  const engine = useEngine();
  const onDesktop = engine.available === true;
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  // Toggle dropdown visibility - only one dropdown open at a time
  const toggleDropdown = (menu) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };
  // Desktop : le menu s'ouvre aussi au SURVOL du bouton d'en-tête (owner
  // 2026-09-08) ; le clic reste. Fermeture différée de 150 ms à la sortie pour
  // laisser traverser l'espace de 8 px (mt-2) entre le bouton et la liste.
  const hoverTimer = useRef(null);
  const openOnHover = (menu) => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
    setActiveDropdown(menu);
  };
  const closeOnLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setActiveDropdown(null), 150);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Close dropdown when navigating to a new page
  useEffect(() => {
    setActiveDropdown(null);
  }, [location.pathname]);

  // Check if the current path matches the given path
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav className="kp-header bg-gradient-to-r from-gray-900 to-black text-white sticky top-0 z-50 shadow-lg" ref={navRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and brand */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" title="Tableau de bord">
              <img
                className="kp-logo h-20 w-auto cursor-pointer"
                src={require('../assets/icon.png')}
                alt="Kipara POS Logo"
              />
            </Link>
          </div>
          
          {/* Desktop menu */}
          <div className="hidden md:flex items-center justify-between w-full gap-1 lg:gap-2 xl:gap-3">
            <div className="flex flex-1 items-center gap-1 lg:gap-2 xl:gap-3">
              {/* Dashboard - all staff (financial cards hidden for sellers) */}
              <Link
                to="/"
                className={`kp-nav flex-1 whitespace-nowrap px-2 py-1.5 lg:px-3 lg:py-2 xl:px-4 xl:py-2.5 rounded-md text-xs lg:text-sm xl:text-base font-medium ${isActive('/') ? 'kp-nav-on' : ''} flex items-center justify-center`}
              >
                <IcHome className="mr-1" />
                <span>Tableau de bord</span>
              </Link>
              
              {/* Sales - For everyone */}
              <div onMouseEnter={() => openOnHover('ventes')} onMouseLeave={closeOnLeave} className="relative flex-1">
                <button
                  onClick={() => toggleDropdown('ventes')}
                  className={`kp-nav w-full whitespace-nowrap px-2 py-1.5 lg:px-3 lg:py-2 xl:px-4 xl:py-2.5 rounded-md text-xs lg:text-sm xl:text-base font-medium ${
                    isActive('/seller-sales') || isActive('/seller-history') || isActive('/service-history') || isActive('/reservations')
                      ? 'kp-nav-on'
                      : ''
                  } flex items-center justify-center`}
                >
                  <IcShoppingCart className="mr-1" />
                  <span>Ventes</span>
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {activeDropdown === 'ventes' && (
                  <div className="kp-dropdown absolute left-0 mt-2 w-48 rounded-xl shadow-xl py-1.5 bg-white ring-1 ring-black ring-opacity-5 z-10 border border-gray-100">
                    <Link
                      to="/seller-sales"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Nouvelle vente
                    </Link>
                    <Link
                      to="/reservations"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Réservation
                    </Link>
                    <Link
                      to="/seller-history"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Historique de vente
                    </Link>
                    <Link
                      to="/service-history"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Historique service
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Products Dropdown - list for all staff; add/promo/catégories admin-only */}
              <div onMouseEnter={() => openOnHover('products')} onMouseLeave={closeOnLeave} className="relative flex-1">
                <button
                  onClick={() => toggleDropdown('products')}
                  className={`kp-nav w-full whitespace-nowrap px-2 py-1.5 lg:px-3 lg:py-2 xl:px-4 xl:py-2.5 rounded-md text-xs lg:text-sm xl:text-base font-medium ${
                    isActive('/products') || isActive('/products/add') || isActive('/products/promos')
                      ? 'kp-nav-on'
                      : ''
                  } flex items-center justify-center`}
                >
                  <IcPackageOpen className="mr-1" />
                  <span>Produits</span>
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {activeDropdown === 'products' && (
                  <div className="kp-dropdown absolute left-0 mt-2 w-48 rounded-xl shadow-xl py-1.5 bg-white ring-1 ring-black ring-opacity-5 z-10 border border-gray-100">
                    <Link
                      to="/products/add"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Ajouter un produit
                    </Link>
                    <Link
                      to="/products"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Liste des produits
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/products/promos"
                        className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                      >
                        Ajouter un promo
                      </Link>
                    )}
                    {/* « Catégories » retiré du menu Produits → Paramètres > Catégories produits (owner 2026-09-08). */}
                  </div>
                )}
              </div>
              

              
              {/* Clients Dropdown - all staff (edit is blocked elsewhere for sellers) */}
              <div onMouseEnter={() => openOnHover('clients')} onMouseLeave={closeOnLeave} className="relative flex-1">
                <button
                  onClick={() => toggleDropdown('clients')}
                  className={`kp-nav w-full whitespace-nowrap px-2 py-1.5 lg:px-3 lg:py-2 xl:px-4 xl:py-2.5 rounded-md text-xs lg:text-sm xl:text-base font-medium ${isActive('/clients') || (location.pathname.startsWith('/clients/') && !location.pathname.startsWith('/clients/categories')) ? 'kp-nav-on' : ''} flex items-center justify-center`}
                >
                  <IcUsers className="mr-1" />
                  <span>Clients</span>
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {activeDropdown === 'clients' && (
                  <div className="kp-dropdown absolute left-0 mt-2 w-48 rounded-xl shadow-xl py-1.5 bg-white ring-1 ring-black ring-opacity-5 z-10 border border-gray-100">
                    <Link
                      to="/clients/add"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Ajouter un client
                    </Link>
                    <Link
                      to="/clients"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Liste des clients
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Achat Dropdown - visible to all staff; facturation entries stay
                  restricted to admin / sellers with facturation access, while
                  « Nouvelle commande », Fournisseurs and Laboratoires are open
                  to every seller account (owner request). */}
              <div onMouseEnter={() => openOnHover('facturation')} onMouseLeave={closeOnLeave} className="relative flex-1">
                <button
                  onClick={() => toggleDropdown('facturation')}
                  className={`kp-nav w-full whitespace-nowrap px-2 py-1.5 lg:px-3 lg:py-2 xl:px-4 xl:py-2.5 rounded-md text-xs lg:text-sm xl:text-base font-medium ${
                    ['/suppliers', '/facturation', '/laboratoires', '/commandes'].some((p) => location.pathname.startsWith(p))
                      ? 'kp-nav-on'
                      : ''
                  } flex items-center justify-center`}
                >
                  <IcShoppingBag className="mr-1" />
                  <span>Achat</span>
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {activeDropdown === 'facturation' && (
                  <div className="kp-dropdown absolute left-0 mt-2 w-48 rounded-xl shadow-xl py-1.5 bg-white ring-1 ring-black ring-opacity-5 z-10 border border-gray-100">
                    {/* Owner 2026-09-21 : « Ajouter Facturation » est ouvert à TOUS les
                        comptes, vendeur compris ; la liste reste sous « Accès facturation ». */}
                    <Link
                      to="/facturation/add"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Ajouter Facturation
                    </Link>
                    {(isAdmin || hasFacturationAccess) && (
                      <Link
                        to="/facturation/list"
                        className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                      >
                        Liste Facturation
                      </Link>
                    )}
                    <Link
                      to="/commandes/a-commander"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Commande
                    </Link>
                    {/* Une seule entrée : la page Fournisseurs porte le bouton
                        de bascule vers Laboratoires (et inversement). */}
                    <Link
                      to="/suppliers"
                      className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                    >
                      Fournisseur et laboratoires
                    </Link>
                  </div>
                )}
              </div>
              

              
              {/* Services retiré de la barre — déplacé dans le menu Paramètres
                  (owner Studio 2026-08-13 : gestion des services = config). */}

            </div>

            {/* Paramètres — Admin only, ancré à l'extrémité droite du header
                (logo au début, engrenage à la fin, quelle que soit la largeur).
                Dropdown = Paramètres + Services + Déconnexion.
                « Gestion utilisateurs » lives inside the Paramètres page. */}
            {isAdmin && (
                <div onMouseEnter={() => openOnHover('users')} onMouseLeave={closeOnLeave} className="relative">
                  <button
                    onClick={() => toggleDropdown('users')}
                    className={`kp-nav w-full whitespace-nowrap px-2 py-1.5 lg:px-3 lg:py-2 xl:px-4 xl:py-2.5 rounded-md text-xs lg:text-sm xl:text-base font-medium ${isActive('/settings') || isActive('/devis') || location.pathname.startsWith('/clients/categories') || location.pathname.startsWith('/categories') ? 'kp-nav-on' : ''} flex items-center justify-center`}
                    title="Paramètres"
                  >
                    <IcSettings className="mr-1" />
                    <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {activeDropdown === 'users' && (
                    <div className="kp-dropdown absolute right-0 mt-2 w-52 rounded-xl shadow-xl py-1.5 bg-white ring-1 ring-black ring-opacity-5 z-10 border border-gray-100">
                      {/* Devis en tête du menu, au-dessus des statistiques (owner 2026-09-17). */}
                      <Link
                        to="/devis"
                        className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                      >
                        <div className="flex items-center">
                          <IcFileText className="mr-2 text-gray-500" />
                          <span>Devis</span>
                        </div>
                      </Link>
                      {/* Statistiques (owner 2026-08-16). */}
                      <Link
                        to="/statistiques/ventes"
                        className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                      >
                        <div className="flex items-center">
                          <IcLineChart className="mr-2 text-gray-500" />
                          <span>Statistique de ventes</span>
                        </div>
                      </Link>
                      <Link
                        to="/statistiques/generale"
                        className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                      >
                        <div className="flex items-center">
                          <IcBarChart className="mr-2 text-gray-500" />
                          <span>Statistique générale</span>
                        </div>
                      </Link>
                      <div className="my-1 border-t border-gray-100" />
                      <Link
                        to="/settings"
                        className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                      >
                        <div className="flex items-center">
                          <IcSettings className="mr-2 text-gray-500" />
                          <span>Paramètres</span>
                        </div>
                      </Link>
                      {onDesktop && (
                        <Link
                          to="/poste"
                          className="kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                        >
                          <div className="flex items-center">
                            <IcCpu className="mr-2 text-gray-500" />
                            <span>Ce poste</span>
                            {engine.status && (engine.status.pending + engine.status.conflicts + engine.status.blocked) > 0 && (
                              <span className={`ml-auto text-xs font-semibold px-1.5 rounded ${engine.status.conflicts ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
                                {engine.status.pending + engine.status.conflicts + engine.status.blocked}
                              </span>
                            )}
                          </div>
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full text-left kp-item block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:font-semibold"
                      >
                        <div className="flex items-center">
                          <IcLogOut className="mr-2 text-red-500" />
                          <span>Déconnexion</span>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 min-h-[44px] min-w-[44px] rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:bg-gray-700 focus:text-white"
            >
              <svg 
                className="h-6 w-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link 
              to="/" 
              className={`kp-nav block px-3 py-2.5 rounded-md text-base font-medium ${
                isActive('/') 
                  ? 'kp-nav-on' 
                  : ''
              }`}
            >
              Home
            </Link>
            
            {/* Achat Section */}
            <div>
              <button
                onClick={() => toggleDropdown('achat')}
                className="w-full text-left px-3 py-2.5 rounded-md text-base font-medium hover:bg-gray-700 hover:text-white flex justify-between items-center"
              >
                <span>Achat</span>
                <svg 
                  className={`h-4 w-4 transform ${activeDropdown === 'achat' ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {activeDropdown === 'achat' && (
                <div className="ml-4 space-y-1 mt-1">
                  {hasFacturationAccess && (
                    <Link
                      to="/facturation/list"
                      className="block pl-4 pr-2 py-2.5 rounded-md text-base font-medium hover:bg-gray-700 hover:text-white"
                    >
                      Liste Facturations
                    </Link>
                  )}
                  <Link
                    to="/facturation/add"
                    className="block pl-4 pr-2 py-2.5 rounded-md text-base font-medium hover:bg-gray-700 hover:text-white"
                  >
                    Ajouter Facturation
                  </Link>
                  <Link
                    to="/commandes/a-commander"
                    className="block pl-4 pr-2 py-2.5 rounded-md text-base font-medium hover:bg-gray-700 hover:text-white"
                  >
                    Commande
                  </Link>
                  <Link
                    to="/suppliers"
                    className="block pl-4 pr-2 py-2.5 rounded-md text-base font-medium hover:bg-gray-700 hover:text-white"
                  >
                    Fournisseur et laboratoires
                  </Link>
                </div>
              )}
            </div>
            
            {/* Services : retiré de la barre — liens déplacés dans la section
                Users/paramètres ci-dessous (owner Studio 2026-08-13). */}

            {/* Users Management Section */}
            <div>
              <button
                onClick={() => toggleDropdown('users-mobile')}
                className="w-full text-left px-3 py-2.5 rounded-md text-base font-medium hover:bg-gray-700 hover:text-white flex justify-between items-center"
              >
                <div className="flex items-center">
                  <IcUserCog className="mr-2" />
                  <span>Users</span>
                </div>
                <svg 
                  className={`h-4 w-4 transform ${activeDropdown === 'users-mobile' ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {activeDropdown === 'users-mobile' && (
                <div className="ml-4 space-y-1 mt-1">
                  <Link
                    to="/users"
                    className="block pl-4 pr-2 py-2.5 rounded-md text-base font-medium hover:bg-gray-700 hover:text-white"
                  >
                    Manage Users
                  </Link>
                  <Link 
                    to="/users/add" 
                    className="block pl-4 pr-2 py-2.5 rounded-md text-base font-medium hover:bg-gray-700 hover:text-white"
                  >
                    Add New User
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <ManagerModeBar />
    </nav>
  );
};

export default Navigation;
